import React, { useEffect, useState, useRef } from 'react';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { AuthContext } from '../../contexts/AuthContext';
import { getQuickChatEmbedUrl } from '../../utils/quicksight';

const AGENT_ID = 'b94e564b-31ff-4651-bc54-8a6bb84e7941';

const QuickChat: React.FC = () => {
  const { user } = React.useContext(AuthContext) || { user: null };
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user && containerRef.current) {
      initializeQuickChat();
    }
  }, [user]);

  const initializeQuickChat = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const embedUrl = await getQuickChatEmbedUrl();
      if (!embedUrl) {
        throw new Error('No embed URL received');
      }

      const embeddingContext = await createEmbeddingContext();
      
      await embeddingContext.embedQuickChat(
        {
          url: embedUrl,
          container: containerRef.current!,
          height: '700px',
          width: '100%',
        },
        {
          agentOptions: {
            fixedAgentId: AGENT_ID,
          },
        }
      );

      setIsLoading(false);
    } catch (err) {
      console.error('Error initializing Quick Chat:', err);
      setError(err instanceof Error ? err.message : 'Failed to load Quick Chat');
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-light rounded-qnav">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-secondary-900 mb-2">Quick Chat Unavailable</h3>
          <p className="text-secondary-600 mb-4">{error}</p>
          <button onClick={initializeQuickChat} className="qnav-btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 bg-surface-light rounded-qnav overflow-hidden">
        {isLoading && (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="qnav-loading-spinner mx-auto mb-4"></div>
              <p className="text-secondary-600">Loading Quick Chat...</p>
            </div>
          </div>
        )}
        <div ref={containerRef} style={{ display: isLoading ? 'none' : 'block', height: '100%' }} />
      </div>
    </div>
  );
};

export { QuickChat };
